import { sankey as d3Sankey, sankeyCenter } from 'd3-sankey';

import { selector } from '../selector';
import type { Data, Node, Link } from '../types';
import type { SankeyNode, SankeyLink } from 'd3-sankey';

import { keys, compact } from '@/utilities/fp';
import {
  NODE_NAMES,
  GROUP_TYPES,
  NODE_WIDTH,
  NODE_PADDING,
  INTEGRATION_TIMES,
  RECAL_HEIGHT_INCREASE,
  NUMBER_OF_RECAL_THIN_CHART,
} from '../const';
import {
  initRevenueSegmentData,
  getExpensesOutData,
  isThinChart,
  getGrossProfitNodes,
  getRevenueOutLinks,
  getEarningsLossOutLinks,
  getLossGrossProfitOutLinks,
  getGrossProfitOutLinks,
} from './utils';

export type RawDataType = ReturnType<typeof selector>;

type CalcSankeyDataParams = {
  rawData: RawDataType;
  width: number;
  height: number;
};

type RetriableCalcSankeyDataParams = CalcSankeyDataParams & {
  numberOfTry?: number;
};

export const NUMBER_OF_RETRY_CALC_SANKEY = 10;

export const calculateChartHeight = (rawData: RawDataType, height: number) => {
  const industryAnalysisPast = rawData?.industryAnalysisPast || {};
  const revenueSegments =
    industryAnalysisPast &&
    (industryAnalysisPast['revenue_segments'] as {
      [key: string]: number;
    });

  const numberOfSegments = keys(revenueSegments).length;

  switch (numberOfSegments) {
    case 1:
      return height / 2;
    case 2:
      return height / 2;
    default:
      return height;
  }
};

type GetEarningsDataParams = {
  earnings: number;
  expenses: number;
  grossProfit: number;
};
const getEarningsData = ({
  earnings,
  expenses,
  grossProfit,
}: GetEarningsDataParams) => {
  const earningsGainNode = earnings >= 0 && {
    name: NODE_NAMES.EARNINGS_GAIN,
    key: 'Earnings',
    groupType: GROUP_TYPES.COST_REVENUE,
  };

  const earningsLossNode = earnings < 0 && {
    name: NODE_NAMES.EARNINGS_LOSS,
    key: 'Earnings',
    groupType: GROUP_TYPES.LOSS,
  };

  const earningsLossOutLinks = getEarningsLossOutLinks({
    earnings,
    expenses,
    grossProfit,
  });

  return {
    earningsGainNode,
    earningsLossNode,
    earningsLossOutLinks,
  };
};

export const calcSankeyData = ({
  rawData,
  width,
  height,
}: CalcSankeyDataParams) => {
  let chartHeight = calculateChartHeight(rawData, height);
  const sankey = d3Sankey();

  sankey.size([width, chartHeight]);
  sankey.nodeId((d: SankeyNode<any, any>) => d.name);
  sankey.nodeWidth(NODE_WIDTH);
  sankey.nodeSort(() => null);
  sankey.linkSort(() => null);
  sankey.nodePadding(NODE_PADDING);
  sankey.iterations(INTEGRATION_TIMES);
  sankey.nodeAlign(sankeyCenter);

  if (typeof rawData !== 'undefined') {
    const { earnings, expenses, grossProfit, revenue, costOfRevenue } = rawData;
    const { nodes: revenueNodes, links: revenueLinks } =
      initRevenueSegmentData(rawData);

    const { nodes: expenseNodes, links: expenseLinks } =
      getExpensesOutData(rawData);

    const expenseMainNode = {
      name: NODE_NAMES.EXPENSES,
      key: 'Expenses',
      groupType: GROUP_TYPES.EXPENSES,
    };

    const grossProfitNodes = getGrossProfitNodes(grossProfit);

    const costOfSalesNode = {
      name: NODE_NAMES.COST_OF_SALES,
      key: 'Cost of Sales',
      groupType: GROUP_TYPES.EXPENSES,
    };

    const revenueOutLinks = getRevenueOutLinks({
      revenue,
      costOfRevenue,
      grossProfit,
    });

    const costOfSalesOutLinks = getLossGrossProfitOutLinks(grossProfit);

    const { earningsGainNode, earningsLossNode, earningsLossOutLinks } =
      getEarningsData({ expenses, earnings, grossProfit });

    const grossProfitOutLinks = getGrossProfitOutLinks({
      grossProfit,
      earnings,
      expenses,
    });

    const data: Data = {
      nodes: compact<Node | false>([
        ...revenueNodes,
        ...grossProfitNodes,
        earningsGainNode,
        expenseMainNode,
        ...expenseNodes,
        costOfSalesNode,
        earningsLossNode,
      ]) as Node[],
      links: compact<Link | false>([
        ...revenueLinks,
        ...revenueOutLinks,
        ...grossProfitOutLinks,
        ...expenseLinks,
        ...earningsLossOutLinks,
        ...costOfSalesOutLinks,
      ]) as Link[],
    };

    const datas = data as unknown as {
      nodes: SankeyNode<any, any>[];
      links: SankeyLink<any, any>[];
    };

    let sankeyData;
    let numberOfTry = 0;

    do {
      sankeyData = sankey(datas);
      const isThin = isThinChart(sankeyData);

      if (isThin && chartHeight + RECAL_HEIGHT_INCREASE < height) {
        chartHeight += RECAL_HEIGHT_INCREASE;
        sankey.size([width, chartHeight]);
      }

      numberOfTry++;
    } while (
      numberOfTry < NUMBER_OF_RECAL_THIN_CHART &&
      isThinChart(sankeyData)
    );

    return { data: sankeyData, chartHeight };
  }

  return {
    data: sankey({ nodes: [], links: [] }),
    chartHeight: height,
  };
};

export const retriableCalcSankeyData = ({
  rawData,
  width,
  height,
  numberOfTry = 0,
}: RetriableCalcSankeyDataParams) => {
  try {
    return calcSankeyData({ rawData, width, height });
  } catch (error) {
    if (numberOfTry < NUMBER_OF_RETRY_CALC_SANKEY) {
      return retriableCalcSankeyData({
        rawData,
        width,
        height,
        numberOfTry: numberOfTry + 1,
      });
    }
    throw error;
  }
};
